<template>
  <div class="user-manage">
    <!-- 工具栏 -->
    <div class="toolbar">
      <div class="filter filter-section">
        <el-form :inline="true" :model="filter">
          <el-form-item label="用户名">
            <BaseInput
              v-model="filter.username"
              placeholder="搜索用户名"
              clearable
              @keyup.enter="handleSearch"
            />
          </el-form-item>
          <el-form-item label="角色">
            <el-select v-model="filter.role" clearable>
              <el-option
                v-for="(label, value) in roleOptions"
                :key="value"
                :label="label"
                :value="value"
              />
            </el-select>
          </el-form-item>
          <el-form-item>
            <BaseButton variant="primary" title="搜索" @click="handleSearch">
              <el-icon><Search /></el-icon><span v-if="!isMobile">搜索</span>
            </BaseButton>
          </el-form-item>
        </el-form>
      </div>
      <div class="actions">
        <BaseButton variant="primary" title="新增用户" @click="handleAdd">
          <el-icon><Plus /></el-icon><span v-if="!isMobile">新增用户</span>
        </BaseButton>
        <BaseButton variant="outline" title="新增教师" @click="handleAddTeacher">
          <el-icon><UserFilled /></el-icon><span v-if="!isMobile">新增教师</span>
        </BaseButton>
        <BaseButton variant="outline" title="权限组管理" @click="router.push('/main/admin/permission-groups')">
          <el-icon><Setting /></el-icon><span v-if="!isMobile">权限组管理</span>
        </BaseButton>
      </div>
    </div>

    <!-- 用户列表（面板包裹，保证表格与分页边框连贯） -->
    <div class="user-manage__panel">
    <ResponsiveTable
      :data="users"
      :fields="userMobileFields"
      row-key="_id"
      :loading="loading"
      class="user-manage__table"
      :row-style="{ height: '60px' }"
      :cell-style="{ padding: '12px 0' }"
      empty-text="暂无用户"
    >
      <el-table-column prop="username" label="用户名" min-width="120" show-overflow-tooltip>
        <template #default="{ row }">
          <div class="user-info">
            <span class="username">{{ row.username }}</span>
          </div>
        </template>
      </el-table-column>

      <el-table-column label="姓名" min-width="96" show-overflow-tooltip>
        <template #default="{ row }">
          {{ displayNameForUser(row) }}
        </template>
      </el-table-column>
      
      <el-table-column prop="role" label="角色" min-width="100" align="center">
        <template #default="{ row }">
          <el-tag :type="row.role === 'superadmin' ? 'danger' : row.role === 'admin' ? 'warning' : 'info'">
            {{ roleOptions[row.role] }}
          </el-tag>
        </template>
      </el-table-column>

      <el-table-column label="权限组" min-width="100" align="center" show-overflow-tooltip>
        <template #default="{ row }">
          <span v-if="row.permission_group?.name">{{ row.permission_group.name }}</span>
          <span v-else style="color: #909399;">无</span>
        </template>
      </el-table-column>

      <el-table-column prop="phone" label="电话" min-width="120" show-overflow-tooltip>
        <template #default="{ row }">
          <span>{{ row.phone }}</span>
        </template>
      </el-table-column>
      
      <el-table-column prop="status" label="状态" min-width="88" align="center">
        <template #default="{ row }">
          <el-tag :type="row.status === 'active' ? 'success' : (row.status === 'inactive' ? 'info' : 'danger')">
            {{ statusOptions[row.status] || row.status }}
          </el-tag>
        </template>
      </el-table-column>
      
      <el-table-column label="操作" width="128" fixed="right" align="center">
        <template #default="{ row }">
          <el-dropdown trigger="hover">
            <BaseButton variant="text" size="small">
              操作
              <el-icon class="el-icon--right">
                <arrow-down />
              </el-icon>
            </BaseButton>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item @click="handleEdit(row)">
                  <el-icon><Edit /></el-icon>编辑
                </el-dropdown-item>
                <el-dropdown-item
                  v-if="canPromoteToTeacher(row)"
                  @click="handleSetAsTeacher(row)"
                >
                  <el-icon><UserFilled /></el-icon>设为教师
                </el-dropdown-item>
                <el-dropdown-item
                  v-if="canDemoteTeacher(row)"
                  @click="handleRemoveTeacher(row)"
                >
                  <el-icon><Delete /></el-icon>取消教师身份
                </el-dropdown-item>
                <el-dropdown-item 
                  v-if="canDeleteUser(row)"
                  @click="handleDelete(row)"
                  divided
                  type="danger"
                >
                  <el-icon><Delete /></el-icon>删除
                </el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </template>
      </el-table-column>

      <template #cell-username="{ row }">
        <div class="m-user-title">
          <span class="username">{{ row.username }}</span>
          <el-tag size="small" :type="row.status === 'active' ? 'success' : (row.status === 'inactive' ? 'info' : 'danger')">
            {{ statusOptions[row.status] || row.status }}
          </el-tag>
        </div>
      </template>
      <template #cell-displayName="{ row }">
        {{ displayNameForUser(row) }}
      </template>
      <template #cell-role="{ row }">
        <el-tag :type="row.role === 'superadmin' ? 'danger' : row.role === 'admin' ? 'warning' : 'info'" size="small">
          {{ roleOptions[row.role] }}
        </el-tag>
      </template>
      <template #actions="{ row }">
        <BaseButton variant="text" size="small" title="编辑" @click="handleEdit(row)">
          <el-icon><Edit /></el-icon>编辑
        </BaseButton>
        <BaseButton
          v-if="canPromoteToTeacher(row)"
          variant="text"
          size="small"
          title="设为教师"
          @click="handleSetAsTeacher(row)"
        >
          设为教师
        </BaseButton>
        <BaseButton
          v-if="canDemoteTeacher(row)"
          variant="text"
          size="small"
          title="取消教师身份"
          @click="handleRemoveTeacher(row)"
        >
          取消教师
        </BaseButton>
        <BaseButton
          v-if="canDeleteUser(row)"
          variant="text"
          size="small"
          title="删除用户"
          @click="handleDelete(row)"
        >
          <el-icon><Delete /></el-icon>删除
        </BaseButton>
      </template>
    </ResponsiveTable>

    <!-- 分页 -->
    <div class="user-manage__pagination">
      <el-pagination
        v-model:current-page="page"
        v-model:page-size="pageSize"
        :total="total"
        :page-sizes="[10, 20, 50, 100]"
        layout="total, sizes, prev, pager, next"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </div>
    </div>

    <!-- 编辑用户对话框 -->
    <el-dialog
      v-model="dialog.visible"
      :title="dialogTitle"
      :width="dialogWidth"
      append-to-body
    >
      <el-alert
        v-if="dialog.type === 'add' && dialog.form.role === 'teacher'"
        type="info"
        :closable="false"
        show-icon
        class="dialog-alert"
      >
        将同时创建教师角色的登录账号与教师档案。
      </el-alert>
      <el-alert
        v-if="dialog.type === 'edit' && dialog.form.role === 'teacher' && !dialog.form.teacherId"
        type="warning"
        :closable="false"
        show-icon
        class="dialog-alert"
      >
        该账号为教师角色但尚无教师档案，请填写科目、校区与简介后保存，系统将创建档案。
      </el-alert>
      <el-form
        ref="dialogFormRef"
        :model="dialog.form"
        :rules="dialogRules"
        label-width="100px"
      >
        <el-form-item label="用户名" prop="username">
          <BaseInput
            v-model="dialog.form.username"
            placeholder="请输入用户名"
            :disabled="dialog.type === 'edit'"
          />
        </el-form-item>

        <el-form-item 
          label="密码" 
          prop="password"
          v-if="dialog.type === 'add'"
        >
          <BaseInput 
            v-model="dialog.form.password" 
            type="password"
            placeholder="请输入密码" 
            show-password
          />
        </el-form-item>

        <el-form-item
          v-if="showTeacherFields"
          label="姓名"
          prop="name"
        >
          <BaseInput v-model="dialog.form.name" placeholder="对外展示姓名（可与用户名不同）" />
        </el-form-item>

        <el-form-item label="手机号" prop="phone">
          <BaseInput v-model="dialog.form.phone" placeholder="请输入手机号" />
        </el-form-item>

        <el-form-item label="角色" prop="role">
          <el-select
            v-model="dialog.form.role"
            placeholder="请选择角色"
          >
            <el-option
              v-for="(label, value) in roleOptions"
              :key="value"
              :label="label"
              :value="value"
            />
          </el-select>
        </el-form-item>

        <el-form-item label="权限组" prop="permission_group">
          <el-select
            v-model="dialog.form.permission_group"
            placeholder="请选择权限组"
            clearable
          >
            <el-option
              v-for="group in permissionGroups"
              :key="group._id"
              :label="group.name"
              :value="group._id"
            />
          </el-select>
        </el-form-item>

        <el-form-item label="状态" prop="status">
          <el-select v-model="dialog.form.status" placeholder="请选择状态">
            <el-option
              v-for="(label, value) in statusOptions"
              :key="value"
              :label="label"
              :value="value"
            />
          </el-select>
        </el-form-item>

        <template v-if="showTeacherFields">
          <el-divider>教师档案</el-divider>
          <el-form-item label="教授科目" prop="subjects">
            <div class="checkbox-group">
              <el-checkbox-group v-model="dialog.form.subjects">
                <el-checkbox label="数学">数学</el-checkbox>
                <el-checkbox label="语文">语文</el-checkbox>
                <el-checkbox label="英语">英语</el-checkbox>
                <el-checkbox label="物理">物理</el-checkbox>
                <el-checkbox label="化学">化学</el-checkbox>
                <el-checkbox label="生物">生物</el-checkbox>
              </el-checkbox-group>
            </div>
          </el-form-item>
          <el-form-item label="所属校区" prop="campuses">
            <div class="checkbox-group">
              <el-checkbox-group v-model="dialog.form.campuses">
                <el-checkbox
                  v-for="campus in campusList"
                  :key="campus._id"
                  :label="campus._id"
                >
                  {{ campus.name }}
                </el-checkbox>
              </el-checkbox-group>
            </div>
          </el-form-item>
          <el-form-item label="简介" prop="introduction">
            <BaseInput
              v-model="dialog.form.introduction"
              type="textarea"
              :rows="3"
              placeholder="请输入教师简介"
            />
          </el-form-item>
        </template>
      </el-form>

      <template #footer>
        <BaseButton variant="outline" @click="dialog.visible = false">取消</BaseButton>
        <BaseButton variant="primary" @click="handleSave" :loading="dialog.saving">
          确定
        </BaseButton>
      </template>
    </el-dialog>

    <!-- 设为教师对话框 -->
    <el-dialog
      v-model="teacherDialog.visible"
      title="设为教师"
      width="520px"
      append-to-body
    >
      <el-form
        ref="teacherFormRef"
        :model="teacherDialog.form"
        :rules="teacherDialog.rules"
        label-width="100px"
      >
        <el-form-item label="用户" prop="username">
          <BaseInput v-model="teacherDialog.form.username" disabled />
        </el-form-item>
        <el-form-item label="教授科目" prop="subjects">
          <el-checkbox-group v-model="teacherDialog.form.subjects">
            <el-checkbox label="数学">数学</el-checkbox>
            <el-checkbox label="语文">语文</el-checkbox>
            <el-checkbox label="英语">英语</el-checkbox>
            <el-checkbox label="物理">物理</el-checkbox>
            <el-checkbox label="化学">化学</el-checkbox>
            <el-checkbox label="生物">生物</el-checkbox>
          </el-checkbox-group>
        </el-form-item>
        <el-form-item label="所属校区" prop="campuses">
          <el-checkbox-group v-model="teacherDialog.form.campuses">
            <el-checkbox
              v-for="campus in campusList"
              :key="campus._id"
              :label="campus._id"
            >
              {{ campus.name }}
            </el-checkbox>
          </el-checkbox-group>
        </el-form-item>
        <el-form-item label="简介" prop="introduction">
          <BaseInput
            v-model="teacherDialog.form.introduction"
            type="textarea"
            :rows="3"
            placeholder="请输入教师简介"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <BaseButton variant="outline" @click="teacherDialog.visible = false">取消</BaseButton>
        <BaseButton variant="primary" :loading="teacherDialog.saving" @click="handleConfirmSetAsTeacher">
          确定
        </BaseButton>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue'
import { useStore } from 'vuex'
import { useRouter, useRoute } from 'vue-router'
import { ElMessageBox, ElMessage } from 'element-plus'
import { ArrowDown, Edit, Delete, Setting, Plus, Search, UserFilled } from '@element-plus/icons-vue'
import BaseButton from '@/components/base/BaseButton.vue'
import BaseInput from '@/components/base/BaseInput.vue'
import ResponsiveTable from '@/components/ResponsiveTable.vue'
import { useResponsive } from '@/composables/useResponsive'

const { isMobile } = useResponsive()

const userMobileFields = [
  { prop: 'username', label: '用户名', primary: true },
  { prop: 'displayName', label: '姓名' },
  { prop: 'role', label: '角色' },
  { prop: 'phone', label: '电话' },
  { prop: 'status', label: '状态', hideOnMobile: true }
]
import { getUserPermission, getPermissionGroups } from '@/api/permission'
import { getCampuses } from '@/api/campus'
import {
  createTeacher,
  createTeacherWithUser,
  getTeachers,
  removeTeacherByUser,
  updateTeacher
} from '@/api/teacher'

const store = useStore()
const router = useRouter()
const route = useRoute()

const teachersList = ref([])

const getTeacherForUser = (userId) => {
  if (!userId) return null
  const id = String(userId)
  return teachersList.value.find((t) => {
    const uid = t.user?._id != null ? String(t.user._id) : t.user != null ? String(t.user) : ''
    return uid === id
  }) || null
}

const displayNameForUser = (row) => {
  const t = getTeacherForUser(row._id)
  if (t?.name) return t.name
  return row.name || row.username || '—'
}

const loadTeachersIndex = async () => {
  try {
    const res = await getTeachers()
    if (res.status === 'success') {
      teachersList.value = res.data.teachers || []
    }
  } catch (e) {
    console.error('加载教师索引失败:', e)
  }
}

// 常量
const roleOptions = {
  user: '普通用户',
  teacher: '老师',
  academic: '教务',
  admin: '管理员',
  superadmin: '超级管理员'
}

const statusOptions = {
  active: '正常',
  disabled: '禁用',
  inactive: '已删除'
}

// 数据
const loading = computed(() => store.state.admin.loading)
const allUsers = computed(() => store.state.admin.users || [])

const filteredUsers = computed(() => {
  let list = [...allUsers.value]
  const u = filter.username?.trim()
  if (u) list = list.filter((x) => x.username?.includes(u))
  if (filter.role) list = list.filter((x) => x.role === filter.role)
  return list
})

const total = computed(() => filteredUsers.value.length)

const users = computed(() => {
  const start = (page.value - 1) * pageSize.value
  return filteredUsers.value.slice(start, start + pageSize.value)
})

const currentUserRole = computed(() => store.getters['auth/currentUser']?.role)

// 分页和筛选
const page = ref(1)
const pageSize = ref(10)
const filter = reactive({
  username: '',
  role: ''
})

watch(
  () => route.query.role,
  (r) => {
    filter.role = typeof r === 'string' ? r : ''
    page.value = 1
  }
)

// 加载用户列表
const fetchData = async () => {
  await store.dispatch('admin/fetchUsers')
  await loadTeachersIndex()
}

// 页码变化（列表为前端筛选分页，无需重复请求）
const handleSizeChange = (val) => {
  pageSize.value = val
  page.value = 1
}

const handleCurrentChange = (val) => {
  page.value = val
}

const handleSearch = () => {
  page.value = 1
}

const emptyDialogForm = () => ({
  _id: '',
  username: '',
  password: '',
  phone: '',
  name: '',
  role: 'user',
  status: 'active',
  permission_group: '',
  teacherId: '',
  subjects: [],
  campuses: [],
  introduction: ''
})

// 对话框相关
const dialog = reactive({
  visible: false,
  saving: false,
  form: emptyDialogForm(),
  type: 'add'
})

const showTeacherFields = computed(() => {
  if (dialog.type === 'add') return dialog.form.role === 'teacher'
  // 编辑：角色为教师，或已有关联教师档案（如 superadmin 兼任教师）
  return dialog.form.role === 'teacher' || !!dialog.form.teacherId
})

const dialogTitle = computed(() => {
  if (dialog.type === 'add' && dialog.form.role === 'teacher') return '新增教师'
  return dialog.type === 'add' ? '新增用户' : '编辑用户'
})

const dialogWidth = computed(() => (showTeacherFields.value ? '620px' : '500px'))

const dialogRules = computed(() => {
  const base = {
    username: [
      { required: true, message: '请输入用户名', trigger: 'blur' },
      { min: 2, max: 20, message: '长度在 2 到 20 个字符', trigger: 'blur' }
    ],
    phone: [
      { required: true, message: '请输入电话号码', trigger: 'blur' },
      { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号码', trigger: 'blur' }
    ],
    role: [{ required: true, message: '请选择角色', trigger: 'change' }],
    status: [{ required: true, message: '请选择状态', trigger: 'change' }],
    permission_group: [{ required: true, message: '请选择权限组', trigger: 'change' }]
  }
  if (dialog.type === 'add') {
    base.password = [
      { required: true, message: '请输入密码', trigger: 'blur' },
      { min: 6, max: 20, message: '长度在 6 到 20 个字符', trigger: 'blur' }
    ]
  }
  if (showTeacherFields.value) {
    base.name = [{ required: true, message: '请输入姓名', trigger: 'blur' }]
    base.subjects = [{ required: true, message: '请选择教授科目', trigger: 'change' }]
    base.campuses = [{ required: true, message: '请选择所属校区', trigger: 'change' }]
    base.introduction = [{ required: true, message: '请输入教师简介', trigger: 'blur' }]
  }
  return base
})

const dialogFormRef = ref(null)

// 设为教师
const campusList = ref([])
const teacherFormRef = ref(null)
const teacherDialog = reactive({
  visible: false,
  saving: false,
  form: {
    userId: '',
    username: '',
    subjects: [],
    campuses: [],
    introduction: ''
  },
  rules: {
    subjects: [{ required: true, message: '请选择教授科目', trigger: 'change' }],
    campuses: [{ required: true, message: '请选择所属校区', trigger: 'change' }],
    introduction: [{ required: true, message: '请输入教师简介', trigger: 'blur' }]
  }
})

const handleEdit = async (row) => {
  dialog.type = 'edit'
  const t = getTeacherForUser(row._id)
  dialog.form = {
    ...emptyDialogForm(),
    ...row,
    password: '',
    name: t?.name || row.name || row.username || '',
    teacherId: t?._id || '',
    subjects: t?.subjects ? [...t.subjects] : [],
    campuses: t?.campuses?.length
      ? t.campuses.map((c) => c._id || c)
      : [],
    introduction: t?.introduction || ''
  }

  try {
    const res = await getUserPermission(row._id)
    if (res.status === 'success') {
      dialog.form.permission_group = res.data.user.permission_group?._id || ''
    }
  } catch (error) {
    console.error('获取权限失败:', error)
    ElMessage.error('获取权限失败')
  }

  dialog.visible = true
}

const handleSave = async () => {
  if (!dialogFormRef.value) return

  try {
    await dialogFormRef.value.validate()
    dialog.saving = true

    const displayName =
      dialog.form.role === 'teacher'
        ? (dialog.form.name || '').trim() || dialog.form.username
        : (dialog.form.name || '').trim() || dialog.form.username

    if (dialog.type === 'add' && dialog.form.role === 'teacher') {
      const res = await createTeacher({
        username: dialog.form.username,
        password: dialog.form.password,
        role: 'teacher',
        name: displayName,
        phone: dialog.form.phone,
        subjects: dialog.form.subjects,
        introduction: dialog.form.introduction,
        campuses: dialog.form.campuses,
        permission_group: dialog.form.permission_group
      })
      if (res.status === 'success') {
        ElMessage.success('教师账号创建成功')
        dialog.visible = false
        await fetchData()
      }
      return
    }

    const formData = {
      username: dialog.form.username,
      name: displayName,
      phone: dialog.form.phone,
      role: dialog.form.role,
      status: dialog.form.status,
      permission_group: dialog.form.permission_group
    }

    if (dialog.type === 'edit') {
      await store.dispatch('admin/updateUser', {
        id: dialog.form._id,
        data: formData
      })

      if (dialog.form.teacherId) {
        const res = await updateTeacher(dialog.form.teacherId, {
          name: displayName,
          phone: dialog.form.phone,
          subjects: dialog.form.subjects,
          introduction: dialog.form.introduction,
          campuses: dialog.form.campuses
        })
        if (res.status !== 'success') {
          throw new Error(res.message || '更新教师档案失败')
        }
      } else if (dialog.form.role === 'teacher') {
        const res = await createTeacherWithUser({
          userId: dialog.form._id,
          subjects: dialog.form.subjects,
          campuses: dialog.form.campuses,
          introduction: dialog.form.introduction
        })
        if (res.status !== 'success') {
          throw new Error(res.message || '创建教师档案失败')
        }
      }

      ElMessage.success('更新成功')
    } else {
      formData.password = dialog.form.password
      await store.dispatch('admin/createUser', formData)
      ElMessage.success('创建成功')
    }

    dialog.visible = false
    await fetchData()
  } catch (error) {
    console.error(dialog.type === 'add' ? '创建失败:' : '更新失败:', error)
    ElMessage.error(
      error.response?.data?.message || error.message || (dialog.type === 'add' ? '创建失败' : '更新失败')
    )
  } finally {
    dialog.saving = false
  }
}

const handleDelete = async (row) => {
  try {
    await ElMessageBox.confirm(
      '确定要删除该用户吗？此操作不可恢复！',
      '删除确认',
      {
        type: 'warning'
      }
    )
    await store.dispatch('admin/deleteUser', row._id)
    ElMessage.success('删除成功')
    await loadTeachersIndex()
  } catch (error) {
    // 用户取消删除时不显示错误
    if (error !== 'cancel' && error !== 'close') {
      console.error('删除失败:', error)
      ElMessage.error(error.message || '删除失败')
    }
  }
}

const loadCampuses = async () => {
  try {
    const res = await getCampuses()
    if (res.status === 'success') {
      campusList.value = res.data.campuses || []
    }
  } catch (error) {
    console.error('加载校区列表失败:', error)
    ElMessage.error('加载校区列表失败')
  }
}

const handleAdd = () => {
  dialog.form = emptyDialogForm()
  dialog.type = 'add'
  dialog.visible = true
}

const handleAddTeacher = () => {
  dialog.form = emptyDialogForm()
  dialog.form.role = 'teacher'
  dialog.type = 'add'
  dialog.visible = true
}

const canPromoteToTeacher = (user) => {
  if (!user || user.status !== 'active') return false
  // 后端以教师档案为准；角色为 superadmin/admin 等也可能已有关联教师
  if (getTeacherForUser(user._id)) return false
  return user.role !== 'teacher'
}

const canDemoteTeacher = (user) => {
  if (!user || user.status !== 'active') return false
  // 只要存在教师档案即可取消教师身份（与 user.role 是否为 teacher 无关）
  return !!getTeacherForUser(user._id)
}

const handleSetAsTeacher = (user) => {
  teacherDialog.form = {
    userId: user._id,
    username: user.username,
    subjects: [],
    campuses: [],
    introduction: ''
  }
  teacherDialog.visible = true
}

const handleConfirmSetAsTeacher = async () => {
  if (!teacherFormRef.value) return
  try {
    await teacherFormRef.value.validate()
    teacherDialog.saving = true
    const res = await createTeacherWithUser({
      userId: teacherDialog.form.userId,
      subjects: teacherDialog.form.subjects,
      campuses: teacherDialog.form.campuses,
      introduction: teacherDialog.form.introduction
    })
    if (res.status === 'success') {
      ElMessage.success('已成功设置为教师')
      teacherDialog.visible = false
      await fetchData()
    }
  } catch (error) {
    console.error('设为教师失败:', error)
    ElMessage.error(error.response?.data?.message || '设为教师失败')
  } finally {
    teacherDialog.saving = false
  }
}

const handleRemoveTeacher = async (user) => {
  try {
    await ElMessageBox.confirm(
      `确定要取消用户「${user.username}」的教师身份吗？`,
      '提示',
      { type: 'warning' }
    )
    const res = await removeTeacherByUser(user._id)
    if (res.status === 'success') {
      ElMessage.success('已取消教师身份')
      await fetchData()
    }
  } catch (error) {
    if (error !== 'cancel' && error !== 'close') {
      console.error('取消教师身份失败:', error)
      ElMessage.error(error.response?.data?.message || '取消教师身份失败')
    }
  }
}

// 判断是否可以删除用户
const canDeleteUser = (user) => {
  // 超级管理员可以删除所有用户（包括管理员）
  if (currentUserRole.value === 'superadmin') {
    return true
  }
  // 管理员只能删除普通用户
  if (currentUserRole.value === 'admin') {
    return user.role === 'user'
  }
  return false
}

// 权限组列表
const permissionGroups = ref([])

// 加载权限组列表
const loadPermissionGroups = async () => {
  try {
    const res = await getPermissionGroups()
    if (res.status === 'success') {
      permissionGroups.value = res.data.permissionGroups
    }
  } catch (error) {
    console.error('获取权限组列表失败:', error)
    ElMessage.error('获取权限组列表失败')
  }
}

onMounted(() => {
  if (typeof route.query.role === 'string' && route.query.role) {
    filter.role = route.query.role
  }
  fetchData()
  loadPermissionGroups()
  loadCampuses()
})
</script>

<style lang="scss" scoped>
@import '@/styles/mixins.scss';

.m-user-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-width: 0;
  width: 100%;

  .username {
    min-width: 0;
    word-break: break-all;
  }
}

.dialog-alert {
  margin-bottom: var(--space-4);
}

.checkbox-group {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-3);
  padding: var(--space-3);
  border-radius: var(--radius-md);
  background: var(--color-surface-muted);
  border: 1px solid var(--color-border);
}

.user-manage {
  height: 100%;
  background: transparent;
}

.toolbar {
  margin-bottom: var(--space-5);
  padding: var(--space-4);
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--space-4);
  flex-wrap: wrap;
}

.filter {
  flex: 1;
  min-width: 0;

  :deep(.el-form) {
    display: flex;
    gap: var(--space-3);
    flex-wrap: wrap;
    align-items: center;
  }

  :deep(.el-form-item) {
    margin-bottom: 0;
  }
}

.actions {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  flex-shrink: 0;
  flex-wrap: wrap;
}

.user-manage__panel {
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  /* 勿用 overflow:hidden，否则会破坏 el-table 右侧固定列 */
  overflow-x: auto;
  background: var(--color-surface);
  box-shadow: var(--shadow-sm);
}

.user-manage__table {
  width: 100%;
}

.user-manage__panel :deep(.el-table) {
  --el-table-header-bg-color: var(--color-surface-muted);
  --el-table-row-hover-bg-color: var(--color-primary-soft);
  border: none;
  border-radius: 0;
}

.user-manage__panel :deep(.el-table__inner-wrapper::before) {
  display: none;
}

.user-manage__panel :deep(.el-table__header th) {
  color: var(--color-text-primary);
  font-size: var(--font-size-body-sm);
  font-weight: 600;
  border-bottom: 1px solid var(--color-border);
}

.user-manage__panel :deep(.el-table__body td) {
  border-bottom: 1px solid var(--color-border);
}

.user-info {
  display: flex;
  align-items: center;
}

.username {
  font-weight: 600;
  color: var(--color-text-primary);
}

.user-manage__pagination {
  display: flex;
  justify-content: flex-end;
  padding: var(--space-4);
  background: var(--color-surface);
  border-top: 1px solid var(--color-border);
}

:deep(.el-dialog) {
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-md);
}

:deep(.el-dialog__header) {
  margin: 0;
  padding: var(--space-4);
  background: var(--color-surface-muted);
  border-bottom: 1px solid var(--color-border);
}

:deep(.el-dialog__title) {
  color: var(--color-text-primary);
  font-size: var(--font-size-h3);
  font-weight: 600;
}

:deep(.el-dialog__body),
:deep(.el-dialog__footer) {
  padding: var(--space-4);
}

:deep(.el-dialog__footer) {
  border-top: 1px solid var(--color-border);
}

@include mobile {
  .toolbar {
    flex-direction: column;
    padding: var(--space-3);
  }

  .filter.filter-section :deep(.el-form) {
    display: grid;
    grid-template-columns: 1fr;
    gap: 8px;
    width: 100%;
  }

  .filter :deep(.el-select),
  .filter :deep(.base-input) {
    width: 100% !important;
    min-width: 0;
  }

  .filter :deep(.el-form-item) {
    margin-right: 0;
    width: 100%;
  }

  .actions {
    width: 100%;
    flex-wrap: wrap;
  }

  .actions :deep(.base-button) {
    flex: 1;
    min-width: 0;
  }

  .user-manage__pagination {
    justify-content: center;
    flex-wrap: wrap;
  }

  .user-manage__pagination :deep(.el-pagination) {
    justify-content: center;
    flex-wrap: wrap;
    row-gap: 8px;
  }
}
</style>
